import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaTaxi, FaClock, FaCreditCard, FaShieldAlt, FaCar, FaQuestionCircle, FaPhone, FaEnvelope } from 'react-icons/fa';
import { MdSupport, MdSecurity, MdPayment, MdCancel, MdBookOnline, MdLocalTaxi } from 'react-icons/md';

const faqs = [
  {
    category: "Booking & Rides",
    icon: <MdBookOnline />,
    questions: [
      {
        q: "How do I book a cab with Plus Cabs?",
        a: "You can book a cab through our website, mobile app, or by calling our customer support. Simply enter your pickup and drop locations, select your preferred vehicle type, and confirm your booking. You'll receive instant confirmation with driver details."
      },
      {
        q: "Can I schedule a ride in advance?",
        a: "Yes! Plus Cabs allows you to schedule rides up to 30 days in advance. Choose the 'Schedule' option while booking, select your date and time, and we'll ensure a cab arrives at your doorstep exactly when you need it."
      },
      {
        q: "What happens if my flight is delayed?",
        a: "Don't worry! We track flight status in real-time for all airport bookings. Your driver will be notified of the delay and will wait for you at no extra cost for up to 60 minutes after your actual landing time."
      },
      {
        q: "Can I cancel my booking?",
        a: "Yes, you can cancel your booking through the app or website. Cancellations made more than 30 minutes before pickup are free. Late cancellations may incur a small fee to compensate our driver's time."
      }
    ]
  },
  {
    category: "Pricing & Payments",
    icon: <MdPayment />,
    questions: [
      {
        q: "How is the fare calculated?",
        a: "Our fares are calculated based on distance, time, and vehicle type. You'll see the estimated fare before confirming your booking. There are no hidden charges – what you see is what you pay."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit/debit cards, UPI (Google Pay, PhonePe, Paytm), net banking, and cash. You can also add multiple payment methods and choose your preferred one at the time of booking."
      },
      {
        q: "Do you charge extra for waiting time?",
        a: "We offer 10 minutes of free waiting time after the scheduled pickup. Beyond that, a nominal waiting charge of ₹2 per minute applies to respect our drivers' time and ensure punctuality."
      },
      {
        q: "Is there a cancellation fee?",
        a: "Cancellations are free up to 30 minutes before pickup. For last-minute cancellations (within 30 minutes of pickup), a fee of ₹50 applies for mini/sedan and ₹100 for SUVs to compensate our drivers."
      }
    ]
  },
  {
    category: "Safety & Support",
    icon: <MdSecurity />,
    questions: [
      {
        q: "Are your drivers verified?",
        a: "Absolutely! Every Plus Cabs driver undergoes thorough background verification, including police verification, driving license check, and regular training. Your safety is our top priority."
      },
      {
        q: "What safety measures do you have?",
        a: "We have multiple safety features: real-time trip tracking, SOS button in the app, share ride status with family, driver details verification, and 24/7 customer support. All vehicles are also equipped with GPS."
      },
      {
        q: "Can I share my trip details with family?",
        a: "Yes! During your ride, you can use the 'Share Trip' feature to send real-time tracking link to your family members. They can see your location, ETA, and driver details without needing the app."
      },
      {
        q: "What if I forget something in the cab?",
        a: "If you've left something behind, immediately contact our support team through the app or call us. We'll connect you with the driver to arrange retrieval. We maintain a lost & found log for 30 days."
      }
    ]
  },
  {
    category: "Vehicles & Services",
    icon: <MdLocalTaxi />,
    questions: [
      {
        q: "What types of cabs do you offer?",
        a: "We offer a wide range of vehicles: Economy (Hatchback), Comfort (Sedan), Premium Sedan, Family SUVs (Ertiga, Innova), Luxury (Innova Crysta, Fortuner), and Group options (Tempo Traveller, Bus)."
      },
      {
        q: "Do you offer outstation trips?",
        a: "Yes! We provide outstation packages with multiple options: one-way drops, round trips, and multi-day rentals. You can choose from per-kilometer pricing or fixed packages with included driver allowance."
      },
      {
        q: "Are your cars well-maintained?",
        a: "All Plus Cabs vehicles undergo regular maintenance checks every 15 days or 3000 kms. We ensure clean interiors, functioning AC, and mechanical fitness before every trip."
      },
      {
        q: "Do you have electric vehicles?",
        a: "Yes! As part of our green initiative, we're introducing electric vehicles in select cities. You can choose our EV option for a cleaner, quieter, and eco-friendly ride."
      }
    ]
  }
];

export default function FAQSection() {
  const [openCategories, setOpenCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const toggleCategory = (category) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Get all unique categories for filter tabs
  const categories = ['All', ...faqs.map(f => f.category)];
  
  // Filter FAQs based on search and category
  const filteredFAQs = faqs.filter(faq => 
    activeCategory === 'All' ? true : faq.category === activeCategory
  ).map(faq => ({
    ...faq,
    questions: faq.questions.filter(q => 
      q.q.toLowerCase().includes(searchTerm.toLowerCase()) || 
      q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(faq => faq.questions.length > 0);

  return (
    <div style={styles.pageContainer}>
      {/* Header Section */}
      <div style={styles.header}>
        <div style={styles.headerBadge}>
          <FaQuestionCircle style={styles.headerIcon} /> FAQ
        </div>
        <h1 style={styles.title}>
          How Can We <span style={styles.titleAccent}>Help You?</span>
        </h1>
        <p style={styles.subtitle}>
          Find answers to commonly asked questions about our services
        </p>
      </div>

      {/* Search Bar - Mobile Friendly */}
      <div style={styles.searchContainer}>
        <div style={styles.searchWrapper}>
          <input
            type="text"
            placeholder="Search your question..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <span style={styles.searchIcon}>🔍</span>
        </div>
      </div>

      {/* Category Filter Tabs - Scrollable on Mobile */}
      <div style={styles.tabsContainer}>
        <div style={styles.tabsScroll}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                ...styles.tabButton,
                ...(activeCategory === cat ? styles.tabButtonActive : {})
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* FAQ Accordion */}
      <div style={styles.faqContainer}>
        {filteredFAQs.length > 0 ? (
          filteredFAQs.map((section, idx) => (
            <div key={idx} style={styles.categorySection}>
              {/* Category Header */}
              <div 
                style={styles.categoryHeader}
                onClick={() => toggleCategory(section.category)}
              >
                <div style={styles.categoryTitleWrapper}>
                  <span style={styles.categoryIcon}>{section.icon}</span>
                  <h2 style={styles.categoryTitle}>{section.category}</h2>
                </div>
                <div style={styles.categoryToggle}>
                  {openCategories[section.category] ? 
                    <FaChevronUp color="#FFD700" size={20} /> : 
                    <FaChevronDown color="#999" size={20} />
                  }
                </div>
              </div>

              {/* Questions */}
              {openCategories[section.category] && (
                <div style={styles.questionsList}>
                  {section.questions.map((item, qIdx) => (
                    <FAQItem key={qIdx} question={item.q} answer={item.a} />
                  ))}
                </div>
              )}
            </div>
          ))
        ) : (
          <div style={styles.noResults}>
            <p style={styles.noResultsText}>No matching questions found</p>
            <button 
              style={styles.clearButton}
              onClick={() => {
                setSearchTerm('');
                setActiveCategory('All');
              }}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Contact Support - Sticky on Mobile */}
      <div style={styles.contactSupport}>
        <p style={styles.contactText}>Still have questions?</p>
        <div style={styles.contactButtons}>
          <a href="tel:+917349519354" style={styles.contactButton}>
            <FaPhone style={styles.contactIcon} /> Call Us
          </a>
          <a href="mailto:support@pluscabs.com" style={styles.contactButton}>
            <FaEnvelope style={styles.contactIcon} /> Email
          </a>
        </div>
      </div>
    </div>
  );
}

// Individual FAQ Item Component
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={styles.faqItem}>
      <div 
        style={styles.questionRow}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span style={styles.questionText}>{question}</span>
        <span style={styles.questionIcon}>
          {isOpen ? '−' : '+'}
        </span>
      </div>
      {isOpen && (
        <div style={styles.answerBox}>
          <p style={styles.answerText}>{answer}</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  pageContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '20px 16px 40px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    backgroundColor: '#FFFFFF',
    minHeight: '100vh',
  },
  header: {
    textAlign: 'center',
    marginBottom: 24,
  },
  headerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFD700',
    padding: '8px 16px',
    borderRadius: 30,
    fontSize: 14,
    fontWeight: 600,
    color: '#000000',
    marginBottom: 16,
  },
  headerIcon: {
    fontSize: 16,
  },
  title: {
    fontSize: 'clamp(28px, 6vw, 42px)',
    fontWeight: 700,
    color: '#000000',
    marginBottom: 10,
    lineHeight: 1.2,
  },
  titleAccent: {
    color: '#FFD700',
    display: 'block',
    '@media (min-width: 768px)': {
      display: 'inline',
    },
  },
  subtitle: {
    fontSize: 'clamp(14px, 4vw, 16px)',
    color: '#666666',
    lineHeight: 1.5,
    maxWidth: 600,
    margin: '0 auto',
  },
  searchContainer: {
    marginBottom: 20,
    padding: '0 4px',
  },
  searchWrapper: {
    position: 'relative',
    maxWidth: 600,
    margin: '0 auto',
  },
  searchInput: {
    width: '100%',
    padding: '16px 20px 16px 50px',
    fontSize: 16,
    border: '2px solid #FFE680',
    borderRadius: 30,
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(255,215,0,0.15)',
    ':focus': {
      borderColor: '#FFD700',
      boxShadow: '0 6px 16px rgba(255,215,0,0.25)',
    },
  },
  searchIcon: {
    position: 'absolute',
    left: 20,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 18,
    color: '#FFD700',
  },
  tabsContainer: {
    marginBottom: 24,
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  tabsScroll: {
    display: 'flex',
    gap: 10,
    paddingBottom: 8,
    minWidth: 'max-content',
  },
  tabButton: {
    padding: '10px 20px',
    borderRadius: 30,
    border: '1.5px solid #FFE680',
    backgroundColor: '#FFFFFF',
    fontSize: 14,
    fontWeight: 600,
    color: '#666666',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#FFF9E6',
    },
  },
  tabButtonActive: {
    backgroundColor: '#FFD700',
    borderColor: '#FFD700',
    color: '#000000',
  },
  faqContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginBottom: 32,
  },
  categorySection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    overflow: 'hidden',
    border: '1px solid #FFE680',
    boxShadow: '0 4px 12px rgba(255,215,0,0.1)',
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: '#FFFDF0',
    cursor: 'pointer',
    borderBottom: '1px solid #FFE680',
  },
  categoryTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  categoryIcon: {
    fontSize: 24,
    color: '#FFD700',
    display: 'flex',
    alignItems: 'center',
  },
  categoryTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#000000',
    margin: 0,
  },
  categoryToggle: {
    display: 'flex',
    alignItems: 'center',
  },
  questionsList: {
    padding: '16px',
  },
  faqItem: {
    marginBottom: 12,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    border: '1px solid #FFE680',
    overflow: 'hidden',
    ':last-child': {
      marginBottom: 0,
    },
  },
  questionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    gap: 12,
  },
  questionText: {
    fontSize: 15,
    fontWeight: 500,
    color: '#000000',
    lineHeight: 1.4,
    flex: 1,
  },
  questionIcon: {
    fontSize: 22,
    fontWeight: 600,
    color: '#FFD700',
    minWidth: 24,
    textAlign: 'center',
  },
  answerBox: {
    padding: '0 16px 16px 16px',
    backgroundColor: '#FFFDF0',
  },
  answerText: {
    fontSize: 14,
    color: '#444444',
    lineHeight: 1.6,
    margin: 0,
  },
  noResults: {
    textAlign: 'center',
    padding: '40px 20px',
    backgroundColor: '#FFFDF0',
    borderRadius: 20,
    border: '1px solid #FFE680',
  },
  noResultsText: {
    fontSize: 16,
    color: '#666666',
    marginBottom: 16,
  },
  clearButton: {
    padding: '12px 24px',
    backgroundColor: '#FFD700',
    border: 'none',
    borderRadius: 30,
    fontSize: 14,
    fontWeight: 600,
    color: '#000000',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    ':hover': {
      opacity: 0.9,
    },
  },
  contactSupport: {
    position: 'sticky',
    bottom: 16,
    backgroundColor: '#000000',
    borderRadius: 30,
    padding: '16px 20px',
    marginTop: 24,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    textAlign: 'center',
    border: '2px solid #FFD700',
  },
  contactText: {
    fontSize: 14,
    color: '#FFD700',
    marginBottom: 12,
    fontWeight: 500,
  },
  contactButtons: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
  },
  contactButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    backgroundColor: '#FFD700',
    borderRadius: 30,
    fontSize: 14,
    fontWeight: 600,
    color: '#000000',
    textDecoration: 'none',
    transition: 'transform 0.2s',
    ':hover': {
      transform: 'scale(1.05)',
    },
  },
  contactIcon: {
    fontSize: 14,
  },
};

// Add global styles for font and scroll behavior
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  
  body {
    margin: 0;
    padding: 0;
    background-color: #FFFFFF;
  }
  
  * {
    box-sizing: border-box;
  }
`;

// Inject global styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = globalStyles;
  document.head.appendChild(style);
}